import React, { useState, useEffect } from 'react';

const ACTOR_TYPES = [
  { value: 'state', label: 'Estat' },
  { value: 'non-state', label: 'No Estatal' },
  { value: 'individual', label: 'Individual' },
  { value: 'group', label: 'Grup' },
  { value: 'unknown', label: 'Desconegut' }
];

const SCORE_OPTIONS = [
  { value: 1, label: '1 - Molt Baix' },
  { value: 2, label: '2 - Baix' },
  { value: 3, label: '3 - Mitjà' },
  { value: 4, label: '4 - Alt' },
  { value: 5, label: '5 - Molt Alt' }
];

function ActorForm({ actor, templates, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    actorType: 'unknown',
    intentionScore: null,
    intentionAnalysis: '',
    capabilityScore: null,
    capabilityAnalysis: '',
    opportunityScore: null,
    opportunityAnalysis: ''
  });

  const [errors, setErrors] = useState({});
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);

  useEffect(() => {
    if (actor) {
      setFormData({
        name: actor.name || '',
        description: actor.description || '',
        actorType: actor.actorType || 'unknown',
        intentionScore: actor.intentionScore || null,
        intentionAnalysis: actor.intentionAnalysis || '',
        capabilityScore: actor.capabilityScore || null,
        capabilityAnalysis: actor.capabilityAnalysis || '',
        opportunityScore: actor.opportunityScore || null,
        opportunityAnalysis: actor.opportunityAnalysis || ''
      });
    }
  }, [actor]);

  const applyTemplate = (template) => {
    setFormData(prev => ({
      ...prev,
      name: template.name,
      description: template.description || '',
      actorType: template.actorType || 'unknown'
    }));
    setShowTemplateSelector(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nom és obligatori';
    }

    // Validate that if score is set, analysis must be present (and vice versa)
    if (formData.intentionScore && !formData.intentionAnalysis.trim()) {
      newErrors.intentionAnalysis = "L'anàlisi d'intenció és obligatori";
    }
    if (formData.capabilityScore && !formData.capabilityAnalysis.trim()) {
      newErrors.capabilityAnalysis = "L'anàlisi de capacitat és obligatori";
    }
    if (formData.opportunityScore && !formData.opportunityAnalysis.trim()) {
      newErrors.opportunityAnalysis = "L'anàlisi d'oportunitat és obligatori";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave({
        ...formData,
        intentionScore: formData.intentionScore ? parseInt(formData.intentionScore) : null,
        capabilityScore: formData.capabilityScore ? parseInt(formData.capabilityScore) : null,
        opportunityScore: formData.opportunityScore ? parseInt(formData.opportunityScore) : null
      });
    }
  };

  const calculateThreatScore = () => {
    if (!formData.intentionScore || !formData.capabilityScore || !formData.opportunityScore) {
      return null;
    }
    const i = parseInt(formData.intentionScore);
    const c = parseInt(formData.capabilityScore);
    const o = parseInt(formData.opportunityScore);
    return ((i * c * o) / 12.5).toFixed(1);
  };

  const threatScore = calculateThreatScore();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-surface rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h3 className="text-lg font-semibold text-content">
            {actor ? 'Editar Actor' : 'Nou Actor'}
          </h3>
          <button
            onClick={onCancel}
            className="p-1 text-content-disabled hover:text-content-secondary"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4">
          {/* Template Selector */}
          {!actor && templates && templates.length > 0 && (
            <div className="mb-4">
              <button
                type="button"
                onClick={() => setShowTemplateSelector(!showTemplateSelector)}
                className="text-sm text-accent hover:text-accent-hover flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Usar plantilla
              </button>
              {showTemplateSelector && (
                <div className="mt-2 border border-border rounded-lg p-2 bg-surface-subtle space-y-1">
                  {templates.map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => applyTemplate(t)}
                      className="w-full text-left px-3 py-2 text-sm text-content hover:bg-surface rounded"
                    >
                      <span className="font-medium">{t.name}</span>
                      {t.description && (
                        <span className="text-content-muted ml-2 truncate">- {t.description}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-content-secondary mb-1">
                Nom *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-surface text-content placeholder-content-disabled ${errors.name ? 'border-error/30' : 'border-border-strong'}`}
                placeholder="Nom de l'actor"
              />
              {errors.name && <p className="mt-1 text-sm text-error">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-content-secondary mb-1">
                Descripció
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-border-strong rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-surface text-content placeholder-content-disabled"
                placeholder="Descripció breu de l'actor"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-content-secondary mb-1">
                Tipus d'Actor
              </label>
              <select
                value={formData.actorType}
                onChange={(e) => handleChange('actorType', e.target.value)}
                className="w-full px-3 py-2 border border-border-strong rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-surface text-content"
              >
                {ACTOR_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* I-C-O Analysis */}
          <div className="mt-6 pt-6 border-t border-border">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-content">Anàlisi I-C-O (Fórmula de Singer)</h4>
              {threatScore && (
                <div className="flex items-center">
                  <span className="text-sm text-content-muted mr-2">Puntuació d'Amenaça:</span>
                  <span className={`px-3 py-1 rounded-full text-white font-bold ${
                    threatScore >= 8 ? 'bg-red-500' :
                    threatScore >= 6 ? 'bg-orange-500' :
                    threatScore >= 4 ? 'bg-yellow-500' :
                    'bg-green-500'
                  }`}>
                    {threatScore}
                  </span>
                </div>
              )}
            </div>

            {/* Intention */}
            <div className="mb-6 p-4 bg-surface-subtle rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <label className="font-medium text-content-secondary">Intenció (I)</label>
                <select
                  value={formData.intentionScore || ''}
                  onChange={(e) => handleChange('intentionScore', e.target.value || null)}
                  className="px-3 py-1.5 border border-border-strong rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-surface text-content"
                >
                  <option value="">Seleccionar...</option>
                  {SCORE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-content-muted mb-2">
                Voluntat o desig de l'actor de causar dany o dur a terme l'amenaça
              </p>
              <textarea
                value={formData.intentionAnalysis}
                onChange={(e) => handleChange('intentionAnalysis', e.target.value)}
                rows={3}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-sm bg-surface text-content placeholder-content-disabled ${errors.intentionAnalysis ? 'border-error/30' : 'border-border-strong'}`}
                placeholder="Justifica la puntuació d'intenció basant-te en les evidències..."
              />
              {errors.intentionAnalysis && <p className="mt-1 text-sm text-error">{errors.intentionAnalysis}</p>}
            </div>

            {/* Capacity */}
            <div className="mb-6 p-4 bg-surface-subtle rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <label className="font-medium text-content-secondary">Capacitat (C)</label>
                <select
                  value={formData.capabilityScore || ''}
                  onChange={(e) => handleChange('capabilityScore', e.target.value || null)}
                  className="px-3 py-1.5 border border-border-strong rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-surface text-content"
                >
                  <option value="">Seleccionar...</option>
                  {SCORE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-content-muted mb-2">
                Recursos, habilitats i mitjans de l'actor per executar l'amenaça
              </p>
              <textarea
                value={formData.capabilityAnalysis}
                onChange={(e) => handleChange('capabilityAnalysis', e.target.value)}
                rows={3}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-sm bg-surface text-content placeholder-content-disabled ${errors.capabilityAnalysis ? 'border-error/30' : 'border-border-strong'}`}
                placeholder="Justifica la puntuació de capacitat basant-te en les evidències..."
              />
              {errors.capabilityAnalysis && <p className="mt-1 text-sm text-error">{errors.capabilityAnalysis}</p>}
            </div>

            {/* Opportunity */}
            <div className="p-4 bg-surface-subtle rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <label className="font-medium text-content-secondary">Oportunitat (O)</label>
                <select
                  value={formData.opportunityScore || ''}
                  onChange={(e) => handleChange('opportunityScore', e.target.value || null)}
                  className="px-3 py-1.5 border border-border-strong rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-surface text-content"
                >
                  <option value="">Seleccionar...</option>
                  {SCORE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-content-muted mb-2">
                Condicions i circumstàncies que permeten o faciliten l'acció
              </p>
              <textarea
                value={formData.opportunityAnalysis}
                onChange={(e) => handleChange('opportunityAnalysis', e.target.value)}
                rows={3}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-sm bg-surface text-content placeholder-content-disabled ${errors.opportunityAnalysis ? 'border-error/30' : 'border-border-strong'}`}
                placeholder="Justifica la puntuació d'oportunitat basant-te en les evidències..."
              />
              {errors.opportunityAnalysis && <p className="mt-1 text-sm text-error">{errors.opportunityAnalysis}</p>}
            </div>
          </div>

          {/* Formula explanation */}
          <div className="mt-4 p-3 bg-accent-subtle border border-accent/30 rounded-lg text-sm text-accent">
            <p className="font-medium mb-1">Fórmula de Singer:</p>
            <p>Amenaça = (I × C × O) / 12.5</p>
            <p className="text-xs mt-1 text-accent">
              La puntuació final es normalitza a una escala de 0-10
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="p-4 border-t border-border flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-content-secondary border border-border-strong rounded hover:bg-surface-subtle"
          >
            Cancel·lar
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="px-4 py-2 text-sm bg-accent text-white rounded hover:bg-accent-hover"
          >
            {actor ? 'Guardar Canvis' : 'Crear Actor'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ActorForm;
